import { motion } from "framer-motion";
import { Building2, Factory, ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const CARDS = [
  {
    icon: Building2,
    tag: "For Clinics",
    title: "No more waiting rooms.",
    points: [
      "Patients check themselves in — no nurse needed for intake",
      "Opens a new revenue stream from remote care programs",
      "Results flow straight into your medical records system",
    ],
    stat: "0 min",
    statLabel: "spent in a waiting room",
    testid: "roi-card-clinics",
  },
  {
    icon: Factory,
    tag: "For Offices & Worksites",
    title: "A checkup without leaving work.",
    points: [
      "Employees get a full health check on-site in about a minute",
      "Healthier teams and fewer sick days",
      "Fits in the corner of a lobby — no extra room needed",
    ],
    stat: "1 hub",
    statLabel: "does the job of a clinic room",
    testid: "roi-card-enterprise",
  },
];

export const RoiGrid = () => (
  <section id="enterprise" className="py-24 lg:py-32 px-6 sm:px-12 max-w-6xl mx-auto" data-testid="roi-section">
    <motion.h2
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1, ease: EASE }}
      className="font-display font-semibold tracking-tight text-[#3A3A3C] text-3xl sm:text-4xl leading-[1.15] max-w-2xl"
    >
      Good for patients. Great for business.
    </motion.h2>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mt-14">
      {CARDS.map((c, i) => (
        <motion.article
          key={c.testid}
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1, ease: EASE, delay: i * 0.12 }}
          className="group rounded-[2rem] bg-white border border-black/5 shadow-sm p-8 sm:p-10 flex flex-col hover:shadow-[0_30px_80px_-30px_rgba(13,13,14,0.18)] transition-shadow duration-500"
          data-testid={c.testid}
        >
          <div className="flex items-center justify-between">
            <c.icon className="w-6 h-6 text-[#1B2A4A]" />
            <ArrowUpRight className="w-5 h-5 text-[#808080] opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-500" />
          </div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#808080] mt-7">{c.tag}</p>
          <h3 className="font-display font-semibold tracking-tight text-[#3A3A3C] text-xl sm:text-2xl mt-2">
            {c.title}
          </h3>
          <ul className="mt-5 space-y-3 flex-1">
            {c.points.map((p) => (
              <li key={p} className="flex gap-3 text-sm text-[#474747] leading-relaxed">
                <span className="mt-[8px] w-1.5 h-1.5 rounded-full bg-[#00F2FE] shrink-0" />
                {p}
              </li>
            ))}
          </ul>
          <div className="mt-8 pt-6 border-t border-black/5">
            <span className="font-display font-semibold tracking-tight text-3xl text-[#3A3A3C]">{c.stat}</span>
            <p className="text-xs text-[#808080] mt-1">{c.statLabel}</p>
          </div>
        </motion.article>
      ))}
    </div>
  </section>
);
